import React, { useState } from 'react';
import { Alert, Descriptions, Form, Input, Modal, Space, Typography, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import {
  AbandonPreview,
  AbandonWalletProvider,
} from '../../domain/usdt-providers/AbandonWalletProvider';
import SystemIdSearchList from '../../domain/components/system-search-list/SystemIdSearchList';
import { normalizeOtp } from '../../core/utils/otp';

const { Text } = Typography;

const errMsg = (e: any) =>
  e?.response?.data?.displayMessage || e?.response?.data?.message || e?.message || '失敗';

/**
 * 「棄用整個商戶」。
 *
 * 分兩段:先選商戶看預覽,確認數字對了才輸入驗證碼送出。不做成一步是因為這顆按鈕
 * 一次會影響幾百顆錢包,而棄用清單正是批次清空的挑選來源 —— 選錯商戶的代價是
 * 把還在營運的客戶的錢排進「準備掃走」。看到數字才按得下去。
 *
 * 商戶狀態由後端把關(只收 Deactivated / Banned),前端不重複判斷 —— 前端擋只是
 * 體驗,後端擋才是防線。
 */
export function AbandonMerchantModal(props: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { open, onClose, onDone } = props;
  const [form] = Form.useForm();
  const [systemId, setSystemId] = useState<string>('');
  const [preview, setPreview] = useState<AbandonPreview | null>(null);
  const [err, setErr] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const reset = () => {
    form.resetFields();
    setSystemId('');
    setPreview(null);
    setErr('');
  };

  const close = () => {
    reset();
    onClose();
  };

  const doPreview = async (id: string) => {
    setSystemId(id);
    setPreview(null);
    setErr('');
    if (!id) return;
    setLoading(true);
    try {
      setPreview(await AbandonWalletProvider.previewAbandonMerchant(id));
    } catch (e: any) {
      // 商戶還在營運(Activated)也走到這裡 —— 這正是我們要讓操作者看見的訊息。
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    const v = await form.validateFields();
    setLoading(true);
    try {
      const r = await AbandonWalletProvider.abandonMerchant({
        systemId,
        code: normalizeOtp(v.code),
      });
      message.success(
        `已棄用 ${r.inserted} 列(商戶錢包 ${r.mchWallets} · 用戶錢包 ${r.userWallets})`,
      );
      close();
      onDone();
    } catch (e: any) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const total = (preview?.mchWallets ?? 0) + (preview?.userWallets ?? 0);

  return (
    <Modal
      title="棄用整個商戶"
      visible={open}
      onCancel={close}
      onOk={submit}
      okText="確認棄用"
      okButtonProps={{ danger: true, disabled: !preview || total === 0, loading }}
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Form form={form} layout="vertical">
          <Form.Item label="商戶" required>
            <SystemIdSearchList systemId={systemId} onChange={doPreview} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              只有「停用(Deactivated)」或「禁用(Banned)」的商戶可以整批棄用
            </Text>
          </Form.Item>

          {preview && (
            <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="商戶狀態">{preview.status}</Descriptions.Item>
              <Descriptions.Item label="商戶錢包">{preview.mchWallets} 列</Descriptions.Item>
              <Descriptions.Item label="用戶錢包">{preview.userWallets} 列</Descriptions.Item>
              {preview.skippedOtherChannel > 0 && (
                <Descriptions.Item label="排除(非 TRON)">
                  {preview.skippedOtherChannel} 列
                </Descriptions.Item>
              )}
            </Descriptions>
          )}

          {preview && total === 0 && (
            <Alert type="info" showIcon message="這個商戶沒有 TRON 錢包,沒有東西可以棄用" />
          )}

          {preview && total > 0 && (
            <Form.Item
              name="code"
              label="驗證碼"
              rules={[{ required: true, pattern: /^\d{6}$/, message: '請輸入驗證碼' }]}
            >
              <Input
                maxLength={6}
                prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="Google Authenticator"
              />
            </Form.Item>
          )}
        </Form>

        {err && <Alert type="error" showIcon message={err} />}
      </Space>
    </Modal>
  );
}

/**
 * 「單獨棄用錢包」—— 貼地址,不必連坐整個商戶。
 *
 * 用在「這一顆有殘值想收回來」的零星情況。後端會逐顆確認是不是我們有私鑰的
 * 商戶/用戶錢包、以及不是運營錢包 —— 前端不重複判斷,因為前端擋只是體驗,
 * 後端擋才是防線。
 */
export function AbandonWalletsModal(props: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { open, onClose, onDone } = props;
  const [form] = Form.useForm();
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const close = () => {
    form.resetFields();
    setErr('');
    onClose();
  };

  const submit = async () => {
    const v = await form.validateFields();
    // 一行一個,或用逗號分隔 —— 從試算表複製過來通常是換行。
    const addresses = String(v.addresses || '')
      .split(/[\s,]+/)
      .map((x) => x.trim())
      .filter(Boolean);
    if (!addresses.length) {
      setErr('請至少填一個地址');
      return;
    }
    setLoading(true);
    try {
      const r = await AbandonWalletProvider.abandonWallets({
        addresses,
        code: normalizeOtp(v.code),
      });
      if (r.notFound?.length) {
        // 不是錯誤,但一定要講 —— 不然操作者會以為都加好了。
        message.warning(
          `已棄用 ${r.inserted} 列;這些不在商戶/用戶錢包裡,沒有加入:${r.notFound.join(', ')}`,
          8,
        );
      } else {
        message.success(`已棄用 ${r.inserted} 列`);
      }
      close();
      onDone();
    } catch (e: any) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="單獨棄用錢包"
      visible={open}
      onCancel={close}
      onOk={submit}
      okText="確認棄用"
      okButtonProps={{ loading }}
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="不必整個商戶一起棄用"
          description={
            <span>
              只把指定的幾顆錢包加進棄用清單,商戶其他錢包不受影響。
              <br />
              加進來之後,再用「掃描鏈上餘額」和「清空鏈上餘額」把殘值收回。
              <br />
              <b>運營錢包(出款/能量站)不能用這個</b> —— 那些請走「內轉」。
            </span>
          }
        />
        <Alert
          type="warning"
          showIcon
          message="錢包需先「停用」才能棄用"
          description={
            <span>
              棄用前錢包必須已停用(available=0),請先到用戶/商戶錢包頁按「停用」。
              <br />
              批次中只要有一顆還在啟用中(available=1),<b>整批會被拒絕並列出是哪幾顆</b>。
            </span>
          }
        />
        <Form form={form} layout="vertical">
          <Form.Item
            name="addresses"
            label="錢包地址(一行一個,或用逗號分隔)"
            rules={[{ required: true, message: '請填至少一個地址' }]}
          >
            <Input.TextArea rows={5} placeholder={'TXxxxxx...\nTYyyyyy...'} />
          </Form.Item>
          <Form.Item
            name="code"
            label="驗證碼"
            rules={[{ required: true, pattern: /^\d{6}$/, message: '請輸入驗證碼' }]}
          >
            <Input
              maxLength={6}
              prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              placeholder="Google Authenticator"
            />
          </Form.Item>
        </Form>
        {err && <Alert type="error" showIcon message={err} />}
      </Space>
    </Modal>
  );
}

/**
 * 「清空鏈上餘額」與「移出棄用」共用的確認框。
 *
 * 兩個都是不可逆的批次動作,都要驗證碼,差別只在文案和呼叫哪支 API —— 做成兩個
 * 幾乎一樣的元件只會讓之後改文案時漏改一邊。
 */
export function RetireConfirmModal(props: {
  open: boolean;
  action: 'sweep' | 'unabandon';
  addresses: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { open, action, addresses, onClose, onDone } = props;
  const [form] = Form.useForm();
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const isSweep = action === 'sweep';

  const close = () => {
    form.resetFields();
    setErr('');
    onClose();
  };

  const submit = async () => {
    const v = await form.validateFields();
    setLoading(true);
    try {
      if (isSweep) {
        const r = await AbandonWalletProvider.sweepChain({
          addresses,
          code: normalizeOtp(v.code),
        });
        message.success(`已排入清空佇列(${r.queued} 列)—— 背景每分鐘處理一顆錢包`);
      } else {
        const r = await AbandonWalletProvider.unabandon({
          addresses,
          code: normalizeOtp(v.code),
        });
        message.success(`已移出棄用(${r.removed} 列)`);
      }
      close();
      onDone();
    } catch (e: any) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isSweep ? '清空鏈上餘額' : '移出棄用清單'}
      visible={open}
      onCancel={close}
      onOk={submit}
      okText={isSweep ? '確認清空' : '確認移出'}
      okButtonProps={{ danger: true, loading }}
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {isSweep ? (
          <Alert
            type="warning"
            showIcon
            message="這個動作不可逆"
            description={
              <span>
                選中錢包的 USDT 和 TRX 會全數轉到後台設定的退役收款地址
                (<Text code>RETIRE_SWEEP_DEST</Text>)。轉出後無法退回。
                <br />
                餘額為 0 的錢包會自動跳過,不會白燒手續費。
              </span>
            }
          />
        ) : (
          <Alert
            type="info"
            showIcon
            message="把錢包從棄用清單移出"
            description="已經清空過的錢包不能移出 —— 錢已經上鏈轉走了,移掉紀錄就查不到去向。"
          />
        )}

        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="選中錢包">
            {new Set(addresses).size} 顆
          </Descriptions.Item>
        </Descriptions>

        <Form form={form} layout="vertical">
          <Form.Item
            name="code"
            label="驗證碼"
            rules={[{ required: true, pattern: /^\d{6}$/, message: '請輸入驗證碼' }]}
          >
            <Input
              maxLength={6}
              prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              placeholder="Google Authenticator"
            />
          </Form.Item>
        </Form>

        {err && <Alert type="error" showIcon message={err} />}
      </Space>
    </Modal>
  );
}
